// src/pages/Partners.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import {
  partners,
  globalPartners,
  localPartners,
  partnerStats,
  getFeaturedPartners,
  type Partner,
  type PartnerType,
  type PartnerCategory
} from '@/data/mock/partners';
import {
  Globe,
  MapPin,
  Handshake,
  Building2,
  Award,
  Users,
  TrendingUp,
  ExternalLink,
  Search,
  Filter,
  X,
  Star,
  Sparkles,
  Calendar,
  Target,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const Partners = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // State
  const [selectedType, setSelectedType] = useState<PartnerType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  // Type filters
  const typeFilters = [
    { 
      value: 'all' as const, 
      label: { en: 'All Partners', ar: 'جميع الشركاء' },
      icon: Handshake,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      value: 'global' as const, 
      label: { en: 'Global', ar: 'عالميون' },
      icon: Globe,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      value: 'local' as const, 
      label: { en: 'Local', ar: 'محليون' },
      icon: MapPin,
      color: 'from-green-500 to-emerald-500'
    },
    { 
      value: 'sponsor' as const, 
      label: { en: 'Sponsors', ar: 'رعاة' },
      icon: Award,
      color: 'from-orange-500 to-amber-500'
    },
  ];

  // Filter partners
  const filteredPartners = useMemo(() => {
    let result = partners;

    // Type filter
    if (selectedType !== 'all') {
      result = result.filter(p => p.type === selectedType);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.en.toLowerCase().includes(query) ||
        p.name.ar.includes(searchQuery) ||
        p.description.en.toLowerCase().includes(query) ||
        p.description.ar.includes(searchQuery)
      );
    }

    // Featured filter
    if (showFeaturedOnly) {
      result = result.filter(p => p.featured === true);
    }

    return result;
  }, [selectedType, searchQuery, showFeaturedOnly]);

  // Statistics
  const stats = [
    {
      icon: Handshake,
      value: partnerStats.total,
      label: { en: 'Total Partners', ar: 'إجمالي الشركاء' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Globe,
      value: partnerStats.global,
      label: { en: 'Global Partners', ar: 'شركاء عالميون' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: MapPin,
      value: partnerStats.local,
      label: { en: 'Local Partners', ar: 'شركاء محليون' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Calendar,
      value: '10+',
      label: { en: 'Years Experience', ar: 'سنوات خبرة' },
      color: 'from-orange-500 to-amber-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <PageHero
        title={isRTL ? 'شركاؤنا' : 'Our Partners'}
        subtitle={isRTL
          ? 'شراكات استراتيجية مع منظمات عالمية ومحلية رائدة لتطوير الرياضة في فلسطين'
          : 'Strategic partnerships with leading global and local organizations to develop sports in Palestine'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.partners') }
        ]}
      />

      {/* Statistics */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 shadow-xl text-center transition-all"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-3`}>
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="text-3xl font-bold gradient-text mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Partners */}
      {getFeaturedPartners().length > 0 && (
        <section className="py-20 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'شركاء مميزون' : 'Featured Partners'}
                </span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 gradient-text">
                {isRTL ? 'شركاؤنا الاستراتيجيون' : 'Our Strategic Partners'}
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {isRTL
                  ? 'شراكات استراتيجية مع مؤسسات رائدة عالمياً ومحلياً'
                  : 'Strategic partnerships with leading global and local institutions'
                }
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {getFeaturedPartners().map((partner, idx) => (
                <FeaturedPartnerCard
                  key={partner.id}
                  partner={partner}
                  isRTL={isRTL}
                  delay={idx * 0.1}
                  onClick={() => setSelectedPartner(partner)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'جميع الشركاء' : 'All Partners'}
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'تصفح شركاءنا' : 'Browse Our Partners'}
            </h2>
          </motion.div>

          {/* Type Filters */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap max-w-4xl mx-auto">
            {typeFilters.map((filter) => {
              const Icon = filter.icon;
              const isActive = selectedType === filter.value;
              
              return (
                <motion.button
                  key={filter.value}
                  onClick={() => setSelectedType(filter.value)}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      : 'bg-card border-2 border-border hover:border-primary/50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{filter.label[isRTL ? 'ar' : 'en']}</span>
                  {isActive && (
                    <Badge variant="secondary" className="text-xs">
                      {filteredPartners.length}
                    </Badge>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Search & Featured Toggle */}
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? 'ابحث عن شريك...' : 'Search for a partner...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} h-14 text-lg`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-muted-foreground hover:text-foreground`}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Advanced Filters */}
            <div className="flex gap-4 flex-wrap items-center justify-between">
              <div className="flex gap-4 flex-wrap">
                {/* Featured Toggle */}
                <Button
                  variant={showFeaturedOnly ? 'default' : 'outline'}
                  onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
                  className="gap-2"
                >
                  <Star className={`h-4 w-4 ${showFeaturedOnly ? 'fill-current' : ''}`} />
                  {isRTL ? 'المميزون فقط' : 'Featured Only'}
                </Button>
              </div>

              {/* Results Count */}
              <div className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredPartners.length} من ${partners.length} شريك`
                  : `Showing ${filteredPartners.length} of ${partners.length} partners`
                }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            {filteredPartners.length > 0 ? (
              <motion.div
                key={`${selectedType}-${searchQuery}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"
              >
                {filteredPartners.map((partner, idx) => (
                  <PartnerCard
                    key={partner.id}
                    partner={partner}
                    isRTL={isRTL}
                    delay={idx * 0.05}
                    onClick={() => setSelectedPartner(partner)}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-2xl font-bold mb-2">
                  {isRTL ? 'لم يتم العثور على نتائج' : 'No Results Found'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isRTL 
                    ? 'جرّب تعديل الفلاتر أو البحث'
                    : 'Try adjusting your filters or search'
                  }
                </p>
                <Button onClick={() => {
                  setSelectedType('all');
                  setSearchQuery('');
                  setShowFeaturedOnly(false);
                }}>
                  {isRTL ? 'إعادة تعيين الفلاتر' : 'Reset Filters'}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-8">
                <Handshake className="h-10 w-10 text-primary" />
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'كن شريكاً معنا' : 'Become a Partner'}
              </h2>
              
              <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                {isRTL
                  ? 'انضم إلينا في مهمتنا لتطوير الجيل القادم من الرياضيين الفلسطينيين وإحداث تأثير إيجابي في المجتمع من خلال الرياضة'
                  : 'Join us in our mission to develop the next generation of Palestinian athletes and make a positive impact in the community through sports'
                }
              </p>

              {/* Benefits */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {[
                  {
                    icon: Target,
                    title: { en: 'Social Impact', ar: 'تأثير اجتماعي' },
                    desc: { en: 'Make a difference', ar: 'أحدث فرقاً' }
                  },
                  {
                    icon: Users,
                    title: { en: 'Brand Visibility', ar: 'ظهور العلامة التجارية' },
                    desc: { en: 'Reach thousands', ar: 'وصول للآلاف' }
                  },
                  {
                    icon: Award,
                    title: { en: 'Recognition', ar: 'تقدير' },
                    desc: { en: 'Community leader', ar: 'قائد مجتمعي' }
                  }
                ].map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 rounded-2xl bg-card border border-border"
                  >
                    <benefit.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-bold mb-1">{benefit.title[isRTL ? 'ar' : 'en']}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc[isRTL ? 'ar' : 'en']}</p>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="btn-glow gap-2">
                    {isRTL ? 'تواصل معنا' : 'Contact Us'}
                    <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="gap-2">
                  {isRTL ? 'تنزيل الملف التعريفي' : 'Download Partnership Brochure'}
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Detail Modal */}
      <PartnerDetailModal
        partner={selectedPartner}
        isRTL={isRTL}
        onClose={() => setSelectedPartner(null)}
      />
    </div>
  );
};

// ===== PARTNER CARD COMPONENT =====
const PartnerCard = ({ 
  partner, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  partner: Partner; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      whileHover={{ scale: 1.05, y: -5 }}
      onClick={onClick}
      className="group cursor-pointer"
    >
      <div className="relative aspect-video rounded-2xl bg-card border-2 border-border p-6 flex items-center justify-center shadow-lg hover:shadow-2xl hover:border-primary/50 transition-all duration-500 overflow-hidden">
        {/* Featured Badge */}
        {partner.featured && (
          <div className="absolute top-2 right-2 z-10">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          </div>
        )}

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-accent/5 transition-all duration-500" />
        
        {/* Logo - COLORFUL (NO GRAYSCALE!) */}
        <img
          src={partner.logo}
          alt={partner.name.en}
          className="relative z-10 w-full h-full object-contain transition-all duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Shine effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>
    </motion.div>
  );
};

// ===== FEATURED PARTNER CARD =====
const FeaturedPartnerCard = ({ 
  partner, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  partner: Partner; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -10, scale: 1.02 }}
      onClick={onClick}
      className="bg-card border-2 border-primary/30 rounded-3xl overflow-hidden shadow-2xl hover:shadow-primary/20 transition-all cursor-pointer group"
    >
      {/* Logo Section */}
      <div className="aspect-video bg-gradient-to-br from-surface-1 to-muted p-8 flex items-center justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_var(--tw-gradient-stops))] from-primary via-transparent to-transparent" />
        </div>

        {/* Logo - COLORFUL */}
        <img
          src={partner.logo}
          alt={partner.name[isRTL ? 'ar' : 'en']}
          className="relative z-10 w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Featured Badge */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500 text-white text-xs font-bold">
            <Star className="h-3 w-3 fill-current" />
            {isRTL ? 'مميز' : 'FEATURED'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
          {partner.name[isRTL ? 'ar' : 'en']}
        </h3>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {partner.description[isRTL ? 'ar' : 'en']}
        </p>

        {/* Since & Category */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {partner.since && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {isRTL ? 'منذ' : 'Since'} {partner.since}
            </div>
          )}
          {partner.category && (
            <Badge variant="secondary" className="text-xs">
              {partner.category}
            </Badge>
          )}
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          size="sm"
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
        >
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
          <ExternalLink className={`h-3 w-3 ${isRTL ? 'mr-2' : 'ml-2'}`} />
        </Button>
      </div>
    </motion.div>
  );
};

// ===== PARTNER DETAIL MODAL =====
const PartnerDetailModal = ({
  partner,
  isRTL,
  onClose
}: {
  partner: Partner | null;
  isRTL: boolean;
  onClose: () => void;
}) => {
  if (!partner) return null;

  return (
    <Dialog open={!!partner} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold gradient-text">
            {partner.name[isRTL ? 'ar' : 'en']}
          </DialogTitle>
          <DialogDescription>
            {partner.country && `${partner.country} • `}
            {partner.since && `${isRTL ? 'شريك منذ' : 'Partner since'} ${partner.since}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Logo */}
          <div className="aspect-video rounded-2xl bg-gradient-to-br from-surface-1 to-muted p-8 flex items-center justify-center">
            <img
              src={partner.logo}
              alt={partner.name[isRTL ? 'ar' : 'en']}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Description */}
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {isRTL ? 'عن الشريك' : 'About Partner'}
            </h4>
            <p className="text-muted-foreground leading-relaxed">
              {partner.description[isRTL ? 'ar' : 'en']}
            </p>
          </div>

          {/* Partnership Details */}
          {partner.partnershipDetails && (
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Handshake className="h-5 w-5 text-primary" />
                {isRTL ? 'تفاصيل الشراكة' : 'Partnership Details'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {partner.partnershipDetails[isRTL ? 'ar' : 'en']}
              </p>
            </div>
          )}

          {/* Achievements */}
          {partner.achievements && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                {isRTL ? 'الإنجازات' : 'Achievements'}
              </h4>
              <div className="space-y-2">
                {partner.achievements[isRTL ? 'ar' : 'en'].map((achievement, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-surface-1"
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Website Link */}
          {partner.website && (
            <div className="pt-4 border-t">
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <Globe className="h-4 w-4" />
                {isRTL ? 'زيارة الموقع الإلكتروني' : 'Visit Website'}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Partners;
